<template>
    <div>
        <div class="alert alert-success" v-if="sent">
            {{ __('Thank you for your presentation request.') }}
        </div>
        <div v-else>
            <div class="row mb-3">
                <div class="col-12">
                    <label class="form-label">{{ __('Contact name') }}<sup>*</sup></label>
                    <input type="text" class="form-control" :class="{ 'is-invalid' : hasError('contact_name') }" v-model="data.contact_name">
                    <div class="invalid-feedback" v-if="hasError('contact_name')">
                        <span v-for="error in errors.contact_name">{{ error }}</span>
                    </div>
                </div>
            </div>
            <div class="row mb-3">
                <div class="col-12">
                    <label class="form-label">{{ __('Contact organisation') }}<sup>*</sup></label>
                    <input type="text" class="form-control" :class="{ 'is-invalid' : hasError('contact_organisation') }" v-model="data.contact_organisation">
                    <div class="invalid-feedback" v-if="hasError('contact_organisation')">
                        <span v-for="error in errors.contact_organisation">{{ error }}</span>
                    </div>
                </div>
            </div>
            <div class="row mb-3">
                <div class="col-md-6">
                    <label class="form-label">{{ __('Email address') }}<sup>*</sup></label>
                    <input type="email" class="form-control" :class="{ 'is-invalid' : hasError('contact_email') }" v-model="data.contact_email">
                    <div class="invalid-feedback" v-if="hasError('contact_email')">
                        <span v-for="error in errors.contact_email">{{ error }}</span>
                    </div>
                </div>
                <div class="col-md-6">
                    <label class="form-label">{{ __('Phone') }}<sup>*</sup></label>
                    <input type="text" class="form-control" :class="{ 'is-invalid' : hasError('contact_phone') }" v-model="data.contact_phone">
                    <div class="invalid-feedback" v-if="hasError('contact_phone')">
                        <span v-for="error in errors.contact_phone">{{ error }}</span>
                    </div>
                </div>
            </div>
            <hr class="my-5">
            <div class="row mb-3">
                <div class="col-12">
                    <label class="form-label">{{ __('Title') }}<sup>*</sup></label>
                    <input type="text" class="form-control" :class="{ 'is-invalid' : hasError('title') }" v-model="data.title">
                    <div class="invalid-feedback" v-if="hasError('title')">
                        <span v-for="error in errors.title">{{ error }}</span>
                    </div>
                </div>
            </div>
            <div class="mb-3">
                <label class="form-label">{{ __('Please describe the subject in less than 100 words') }}<sup>*</sup></label>
                <textarea class="form-control" :class="{ 'is-invalid' : hasError('description') }" rows="4" v-model="data.description"></textarea>
                <div class="invalid-feedback" v-if="hasError('description')">
                    <span v-for="error in errors.description">{{ error }}</span>
                </div>
            </div>

            <div class="row mb-3">
                <div class="col-12">
                    <label class="form-label">{{ __('Speaker(s)') }}</label>
                    <div class="row mb-3" v-for="(speaker, index) in data.speakers">
                        <hr v-if="index > 0" class="d-md-none mt-3">
                        <div class="col-md-6">
                            <label class="form-label mb-1">{{ __('Name') }}</label>
                            <input class="form-control" :class="{ 'is-invalid' : hasError('speakers.'+ index +'.name') }" v-model="speaker.name">
                        </div>
                        <div class="col-md-6">
                            <label class="form-label mb-1">{{ __('Organisation') }}</label>
                            <div class="d-flex flex-column flex-md-row">
                                <input class="form-control" :class="{ 'is-invalid' : hasError('speakers.'+ index +'.organisation') }" v-model="speaker.organisation">
                                <button v-if="data.speakers.length > 1" class="btn btn-sm btn-link text-danger ms-2 mt-2 mt-md-0" @click.prevent="data.speakers.splice(index, 1)"><i class="fa fa-trash mx-1"></i></button>
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-md-6 offset-md-6 text-end">
                            <button class="btn btn-primary text-white btn-sm" @click.prevent="addSpeaker">{{ __('Add speaker') }}<i class="fa fa-plus"></i> </button>
                        </div>
                    </div>
                </div>
            </div>
            <hr class="my-5">

            <div class="mb-3">
                <label class="form-label">{{ __('Who do you expect to be interested by your presentation?') }}<sup>*</sup></label>
                <textarea class="form-control" :class="{ 'is-invalid' : hasError('target_audience') }" rows="4" v-model="data.target_audience"></textarea>
                <div class="invalid-feedback" v-if="hasError('target_audience')">
                    <span v-for="error in errors.target_audience">{{ error }}</span>
                </div>
            </div>
            <div class="mb-3">
                <label class="form-label" >{{ __('Which communication methods will you apply?') }}<sup>*</sup></label>
                <div class="form-check" :class="{ 'is-invalid' : hasError('presentation_channel_ids') }" v-for="channel in presentationChannels">
                    <input class="form-check-input" type="checkbox" :value="channel.id" :id="'channel-'+ channel.id" v-model="data.presentation_channel_ids">
                    <label class="form-check-label" :for="'channel-'+ channel.id">
                        {{ channel.label[_locale] ? channel.label[_locale] : '' }}
                    </label>
                </div>
                <div class="invalid-feedback" v-if="hasError('presentation_channel_ids')">
                    <span v-for="error in errors.presentation_channel_ids">{{ error }}</span>
                </div>
            </div>

            <hr class="my-5">

            <div class="mb-3">
                <label class="form-label">{{ __('Comments') }}</label>
                <textarea class="form-control" :class="{ 'is-invalid' : hasError('comments') }" rows="4" v-model="data.comments"></textarea>
                <div class="invalid-feedback" v-if="hasError('comments')">
                    <span v-for="error in errors.comments">{{ error }}</span>
                </div>
            </div>
            <div class="mb-3">
                <recaptcha ref="recaptcha" :language="language" :sitekey="siteKey" v-if="siteKey" @verify="captchaVerified"></recaptcha>
                <div class="invalid-feedback d-block" v-if="hasError('recaptcha')">
                    <span v-for="error in errors.recaptcha">{{ error }}</span>
                </div>
            </div>
            <button class="btn btn-lg btn-primary text-white" :class="{ disabled : sending }" @click="send"><i class="fa fa-spin fa-circle-notch" v-if="sending"></i> {{ __('Send') }} <i class="fa fa-chevron-right"></i></button>
        </div>
    </div>
</template>

<script>
    export default {
        props: ['locale'],
        data() {
            return {
                sent : false,
                sending: false,
                errors: null,
                presentationChannels : [],
                siteKey: null,
                language : null,
                data : {
                    contact_name : null,
                    contact_organisation : null,
                    contact_email : null,
                    contact_phone : null,
                    title : null,
                    description : null,
                    speakers : [{ name : '', organisation : ''}],
                    target_audience : null,
                    presentation_channel_ids : [],
                    comments : null,
                    recaptcha : null,
                }
            }
        },
        methods: {
            loadPresentationChannels() {
                axios.get('/api/presentation-channels')
                     .then(response => {
                         this.presentationChannels = response.data;
                     });
            },
            addSpeaker() {
                this.data.speakers.push({name : '', organisation : ''})
            },
            cleanInput() {
                for (let i = 0; i < this.data.speakers.length; i++) {
                    if (!this.data.speakers[i].name && !this.data.speakers[i].organisation) {
                        this.data.speakers.splice(i, 1);
                        i--;
                    }
                }
            },
            send() {
                if (this.sending) {
                    return;
                }

                this.cleanInput();

                this.sending = true;
                axios.post('/'+ this.locale +'/presentation-requests', this.data)
                     .then(response => {
                         this.sending = false;
                         this.sent = true;
                         this.errors = null;
                     }).catch(error => {
                         if (error.response.data && error.response.data.errors) {
                             this.errors = error.response.data.errors;
                         }
                         this.sending = false;

                         if (!this.data.speakers.length) {
                             this.addSpeaker();
                         }

                         this.$refs.recaptcha.reset() // Direct call reset method
                })
            },
            hasError(field) {
                return this.errors && this.errors[field] && this.errors[field].length > 0;
            },
            captchaVerified(token) {
                this.data.recaptcha = token;
            }
        },
        mounted() {
            this.loadPresentationChannels();
            this.language = window._locale;
            this.siteKey = window.google_site_key;
        },
    }
</script>
